import { useQuery } from 'villus';
import { GetEventByIdDocument } from '~/graphql/GetEventById';
import { GetEventsDocument, type GetEventsQuery, type GetEventsQueryVariables } from '~/graphql/GetEvents';

export function useAppEvents(variables: ComputedRef<GetEventsQueryVariables>) {
  // Stores all events fetched so far for infinite scrolling
  const accumulatedEvents = ref<NonNullable<GetEventsQuery['events']>['items'] | []>([]);

  // Tracks the page number of the *last* successful data fetch
  const lastSuccessfulPage = ref(0);

  const { data, error, isFetching } = useQuery<GetEventsQuery>({ query: GetEventsDocument.toString(), variables });

  // Watch for new data arriving from the GraphQL query
  watch(
    () => data.value?.events?.items,
    newItems => {
      // Get the current page from the variables being passed in
      const currentPage = variables.value.currentPage || 1;

      if (newItems) {
        // Filter out any potential null/undefined items
        const validNewItems = newItems.filter(item => !!item);

        // If it's page 1, or we're fetching a page that's not greater than
        // the last one (meaning a "reset" or going back to an earlier page),
        // we replace the accumulated events.
        if (currentPage === 1 || currentPage <= lastSuccessfulPage.value) {
          accumulatedEvents.value = validNewItems;
        } else {
          // Otherwise, we're going forward in pages, so append the new items.
          accumulatedEvents.value = [...accumulatedEvents.value, ...validNewItems];
        }

        // Update the tracker for the last successfully loaded page
        lastSuccessfulPage.value = currentPage;
      }
    },
    // `immediate: true` runs the watch when the component first loads
    { deep: true, immediate: true },
  );

  // Watch for changes in any *other* variables (like search, filters)
  // If these change, we need to reset the accumulated list.
  watch(
    () => {
      // Destructure to get all variables *except* the page number
      // oxlint-disable-next-line no-unused-vars
      const { currentPage, ...otherFilteringVariables } = variables.value;
      return otherFilteringVariables;
    },
    (newOtherVars, oldOtherVars) => {
      // Simple check to see if the "other" variables have actually changed
      // You might need a more robust deep comparison for complex objects
      if (JSON.stringify(newOtherVars) !== JSON.stringify(oldOtherVars)) {
        accumulatedEvents.value = [];
        lastSuccessfulPage.value = 0;
      }
    },
    { deep: true }, // Watch the 'other' variables deeply
  );

  // Your components will now use this accumulated list
  const events = computed(() => accumulatedEvents.value || []);

  // Compute total pages and events from the latest data received
  const totalPages = computed(() => data.value?.events?.total_pages || 1);
  const totalEvents = computed(() => {
    const pageSize = variables.value.pageSize || 10; // Use actual pageSize from variables, default to 10 if not set
    const numPages = totalPages.value;

    if (numPages === 0) {
      return 0; // No pages means no events
    }
    // If we are on the last page, the total number of events is:
    // (number of full pages * pageSize) + items on the last (current) page
    // Assuming the last page contains events.length items.
    // This is an estimation, as we don't truly know the total count
    // without a specific `total_count` field from the backend.
    if (lastSuccessfulPage.value === numPages && events.value.length > 0) {
      return (numPages - 1) * pageSize + events.value.length;
    }

    // If we haven't reached the last page yet, estimate based on full pages
    // or if totalPages is just 1, it's the current events count.
    if (numPages === 1) {
      return events.value.length;
    }

    // Otherwise, assume it's roughly totalPages * pageSize.
    // This is a rough estimate and might be slightly off if the last page
    // has fewer items than pageSize.
    return numPages * pageSize;
  });
  return {
    events, // This is now your continuously growing list
    isFetching,
    error,
    totalPages,
    totalEvents,
  };
}

export function useAppEvent(eventId: number) {
  const { data, error, isFetching } = useQuery({
    query: GetEventByIdDocument.toString(),
    variables: { id: eventId },
  });

  const event = computed(() => data.value?.events?.items?.[0] || null);

  return { event, isFetching, error };
}
